import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import client, { imgUrl, assetUrl, previewIssue } from "@/lib/glossyApi";
import MagazineLayout from "@/components/MagazineLayout";
import { Reveal } from "@/components/Reveal";
import AdBand from "@/components/AdBand";
import MiniAd from "@/components/MiniAd";
import HouseAd from "@/components/HouseAd";
import Seo from "@/components/Seo";
import { cycleIndexFor, editionLabel, editionStart } from "@/lib/issue";

const fmtShort = (dt) => dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

// Fortnightly issue number. Layout is FIXED, always — content changes, the furniture never does.
// In dress-rehearsal mode (editor only) labels follow the previewed edition.
const _pv = previewIssue();
const cycleIndex = _pv ? cycleIndexFor(_pv + "T12:00:00Z") : cycleIndexFor();
const isPreview = cycleIndex < 0;
const issueNo = isPreview ? "0 — Preview" : String(cycleIndex + 1).padStart(2, "0");
const shuffled = (arr) => [...arr].sort(() => Math.random() - 0.5);

const ORDINALS = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth", "Eleventh", "Twelfth"];
const autoEditionName = () => {
  const n = Math.max(1, cycleIndex + 1);
  return ORDINALS[n - 1] ? `The ${ORDINALS[n - 1]} Edition` : `Edition Nº ${n}`;
};
export const BANNER_COLOURS = {
  gold: { bg: "bg-gloss-gold", text: "text-ink", sub: "text-ink/70", btn: "bg-ink text-cream hover:bg-cream hover:text-ink" },
  ink: { bg: "bg-ink", text: "text-cream", sub: "text-cream/70", btn: "bg-gloss-gold text-ink hover:bg-cream" },
  burgundy: { bg: "bg-[#5e1b25]", text: "text-cream", sub: "text-cream/70", btn: "bg-gloss-gold text-ink hover:bg-cream" },
  forest: { bg: "bg-[#1d3a2a]", text: "text-cream", sub: "text-cream/70", btn: "bg-gloss-gold text-ink hover:bg-cream" },
  cream: { bg: "bg-cream border-y border-ink/15", text: "text-ink", sub: "text-ink/60", btn: "bg-ink text-cream hover:bg-gloss-gold hover:text-ink" },
  pink: { bg: "bg-[#E8438A]", text: "text-ink", sub: "text-ink/75", btn: "bg-ink text-cream hover:bg-cream hover:text-ink" },
  ember: { bg: "bg-[#E8641B]", text: "text-ink", sub: "text-ink/80", btn: "bg-ink text-cream hover:bg-cream hover:text-ink" },
};

export default function Home() {
  const [coverStar, setCoverStar] = useState([]);
  const [coverLoaded, setCoverLoaded] = useState(false);
  const [articles, setArticles] = useState([]);
  const [general, setGeneral] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [members, setMembers] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [spotlightArticles, setSpotlightArticles] = useState([]);
  const [editArticles, setEditArticles] = useState([]);
  const [banner, setBanner] = useState({});

  useEffect(() => {
    document.title = "S/ME — The UK's Online SME Magazine";
    client.get("/articles?cover=true&limit=1").then((r) => setCoverStar(r.data)).catch(() => {}).finally(() => setCoverLoaded(true));
    client.get("/articles?type=editorial&limit=12").then((r) => setArticles(r.data)).catch(() => {});
    client.get("/articles?type=editorial&authored_by=magazine&limit=12").then((r) => setGeneral(r.data)).catch(() => {});
    client.get("/articles?type=five_questions&limit=6").then((r) => setInterviews(r.data)).catch(() => {});
    client.get("/members").then((r) => setMembers(r.data)).catch(() => {});
    client.get("/articles?type=blog&limit=6").then((r) => setBlogs(r.data)).catch(() => {});
    client.get("/home-banner").then((r) => setBanner(r.data || {})).catch(() => {});
    client.get("/articles?type=spotlight&limit=12").then((r) => setSpotlightArticles(r.data)).catch(() => {});
    client.get("/articles?type=edit&limit=12").then((r) => setEditArticles(r.data)).catch(() => {});
  }, []);

  const spotlightFor = (mid) => spotlightArticles.find((s) => s.member_id === mid);
  // Cover star = the flagged general editorial (a cover article), full image shown.
  const coverSlide = coverStar[0] || (coverLoaded ? general[0] || articles[0] : undefined);
  // The Edit preview: a rotating window into the Edit page — 3 pieces per refresh + the fixed square ad tile.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const editPreview = useMemo(() => shuffled(editArticles.filter((a) => !a.is_cover)).slice(0, 3), [editArticles]);
  // Interviews: 3 rotating per refresh, always with the member's picture.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const interviewCards = useMemo(() => shuffled(interviews).slice(0, 3), [interviews]);
  const generalArticles = useMemo(
    () => shuffled((general.length ? general : articles).filter((a) => !a.is_cover)).slice(0, 4),
    [general, articles]
  );
  // six member spotlights, one per business category where possible (not the cover)
  // Only members the editor has actually PUBLISHED a Spotlight article for appear here — never automatic.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const eligible = useMemo(() => shuffled(members.filter((m) => m.id !== coverSlide?.member_id && spotlightFor(m.id))), [members, coverSlide?.member_id, spotlightArticles]);
  const seen = new Set();
  const memberSpotlights = [];
  for (const m of eligible) {
    const cat = m.business_category || m.category;
    if (!seen.has(cat)) { seen.add(cat); memberSpotlights.push(m); }
    if (memberSpotlights.length >= 6) break;
  }
  if (memberSpotlights.length < 6) {
    for (const m of eligible) { if (!memberSpotlights.includes(m)) memberSpotlights.push(m); if (memberSpotlights.length >= 6) break; }
  }
  // Cover Members strip: random selection, reshuffled on every page refresh. Always features —
  // if the exclusion filter leaves too few, fall back to the full member pool.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const coverMembers = useMemo(() => {
    let pool = eligible.filter((m) => m.directory_image && !memberSpotlights.slice(0, 2).includes(m));
    if (pool.length < 4) pool = members.filter((m) => m.directory_image);
    return [...pool].sort(() => Math.random() - 0.5).slice(0, 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);
  const deskPosts = useMemo(() => shuffled(blogs).slice(0, 3), [blogs]);
  const heroImg = coverSlide ? imgUrl(coverSlide) : undefined;

  return (
    <MagazineLayout darkNav>
      <Seo
        title="S/ME — The UK's Online SME Magazine"
        description="GQ meets Vogue for the UK's small business owners. Editorial features, cover stars, offers and events from the country's most stylish independents."
        image={heroImg}
        jsonLd={{ "@context": "https://schema.org", "@type": "Organization", name: "S/ME", alternateName: "See Me", description: "The UK's online SME magazine that turns small business owners into editorial stars.", url: typeof window !== "undefined" ? window.location.origin : "" }}
      />

      {/* magazine spine — signature device */}
      <div className="hidden lg:block fixed top-1/2 right-3 -translate-y-1/2 z-40 origin-center rotate-90 pointer-events-none mix-blend-difference">
        <span className="font-mono text-[10px] uppercase tracking-[0.45em] text-cream/90">S/ME · {isPreview ? "Preview Edition" : `Issue Nº ${issueNo}`} · United Kingdom</span>
      </div>

      <HeroCarousel slides={coverSlide ? [coverSlide] : []} ready={coverLoaded} />

      {/* EDITION BANNER — auto edition name, colour + wording controlled in Studio → Editions */}
      {(() => { const c = BANNER_COLOURS[banner.color] || BANNER_COLOURS.gold; return (
      <section className={`${c.bg} px-5 sm:px-8 py-5 sm:py-6`} data-testid="launch-banner">
        <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-baseline gap-4 flex-wrap">
            <span className={`font-mono text-[10px] uppercase tracking-[0.4em] ${c.sub}`}>Welcome to</span>
            <span className={`font-serif text-2xl sm:text-3xl tracking-tight ${c.text} leading-none`} data-testid="banner-title">{banner.title || autoEditionName()}</span>
            <span className={`hidden md:inline font-sans text-sm ${c.sub}`}>{banner.sub || "S/ME is live — the UK's online magazine that turns small business owners into editorial stars."}</span>
          </div>
          <Link to="/join" className={`shrink-0 ${c.btn} px-5 py-2.5 overline text-xs flex items-center gap-2 transition-colors`} data-testid="launch-banner-cta">
            Be in the next one <ArrowRight size={13} />
          </Link>
        </div>
      </section>
      ); })()}

      {/* SIX MEMBER SPOTLIGHTS — one per business category, straight after the cover */}
      {memberSpotlights.length > 0 && (
        <section className="bg-cream pt-4 pb-16 sm:pb-24 px-5 sm:px-8" data-testid="category-spotlights">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-end justify-between border-b border-ink/20 pb-4 mb-10">
              <div>
                <p className="overline text-gloss-silver mb-2">Across the UK</p>
                <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none">The Spotlights</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {memberSpotlights.map((m, i) => {
                const sp = spotlightFor(m.id);
                const to = sp ? `/read/${sp.slug}` : `/member/${m.id}`;
                const img = sp ? imgUrl(sp) : imgUrl(m);
                return (
                <Reveal key={m.id} delay={i * 0.06}>
                  <Link to={to} className="group block relative overflow-hidden bg-ink" data-testid="spotlight-member-card">
                    <div className="relative aspect-[4/3] ed-frame">
                      <img src={img} alt={m.owner_name} className="w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                      <p className="overline text-gloss-gold">Spotlight · {m.business_category || m.category}</p>
                      <h3 className="font-serif text-2xl sm:text-3xl text-cream tracking-tight leading-tight mt-2">{sp ? sp.title : m.business_name}</h3>
                      <p className="font-sans text-sm text-cream/70 mt-1">{m.owner_name ? `${m.owner_name} · ` : ""}{m.business_name} · {m.borough}</p>
                    </div>
                  </Link>
                </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* PROMINENT ADVERT — full-bleed, high on the page */}
      <AdBand space="home_band_a" excludeMemberId={coverSlide?.member_id} />

      {/* THE EDIT — preview of 4 revolving edits, click through to the page */}
      {editPreview.length > 0 && (
      <section className="bg-cream py-16 sm:py-24 px-5 sm:px-8" data-testid="latest-articles">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-end justify-between border-b border-ink/20 pb-4 mb-10">
            <div>
              <p className="overline text-gloss-silver mb-2">{isPreview ? "Preview Edition" : `Issue Nº ${issueNo}`}</p>
              <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none">The Edit</h2>
            </div>
            <Link to="/the-edit" className="overline link-underline flex items-center gap-2 shrink-0" data-testid="all-stories-link">Enter the Edit <ArrowUpRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {editPreview.map((a, i) => <FeatureCard key={a.id} a={a} ratio="aspect-[3/4]" delay={i * 0.06} />)}
            {Array.from({ length: Math.max(0, 3 - editPreview.length) }).map((_, i) => (
              <Link key={`res-${i}`} to="/join" className="group flex flex-col justify-between aspect-[3/4] border border-ink/15 bg-secondary/50 p-6 hover:border-ink/40 transition-colors" data-testid={`edit-reserved-${i}`}>
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-ink/35">The Edit</p>
                <div>
                  <p className="font-serif text-2xl tracking-tight leading-tight text-ink/70">Reserved for a member's story.</p>
                  <span className="inline-flex items-center gap-2 mt-4 overline text-ink/60 group-hover:text-ink group-hover:gap-3 transition-all">Claim your place <ArrowUpRight size={12} /></span>
                </div>
              </Link>
            ))}
            <MiniAd variant="square" mode="branded" space="home_square" excludeMemberId={coverSlide?.member_id} />
          </div>
        </div>
      </section>
      )}

      {/* THE NEWSROOM — magazine-authored general articles */}
      {generalArticles.length > 0 && (
        <section className="bg-ink text-cream py-16 sm:py-24 px-5 sm:px-8 relative grain" data-testid="sme-desk">
          <div className="max-w-[1600px] mx-auto relative z-10">
            <div className="flex items-end justify-between border-b border-cream/20 pb-4 mb-10">
              <div>
                <p className="overline text-gloss-gold mb-3">Written by S/ME</p>
                <h2 className="font-serif text-4xl sm:text-6xl tracking-tight">The Newsroom</h2>
              </div>
              <Link to="/newsroom" className="overline link-underline flex items-center gap-2 text-cream shrink-0">More editorial <ArrowUpRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {generalArticles.map((a, i) => (
                <Reveal key={a.id} delay={i * 0.06}>
                  <Link to={`/read/${a.slug}`} className="group block" data-testid="general-card">
                    <div className="relative overflow-hidden aspect-[3/4] bg-ink ed-frame">
                      <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-5">
                        <p className="overline text-gloss-gold">{a.category}</p>
                        <h3 className="font-serif text-2xl tracking-tight leading-tight mt-2">{a.title}</h3>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EDITOR'S LETTER — the voice of the house, after the reader is already in */}
      <EditorsManifesto />

      {/* MEMBER ADVERT — brand-styled, never spammy */}
      <AdBand space="home_band_b" excludeMemberId={coverSlide?.member_id} />

      {/* INTERVIEWS — 3 rotating cards, always with the member's picture */}
      {interviewCards.length > 0 && (
        <section className="bg-cream pt-20 pb-16 sm:pb-24 px-5 sm:px-8">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-ink/20 pb-6 mb-10">
              <div>
                <p className="overline text-gloss-gold mb-2">In their own words</p>
                <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none">The Interviews</h2>
                <p className="font-sans text-ink/60 mt-3 max-w-md">Every member, interviewed — the questions we ask everyone who makes the cover.</p>
              </div>
              <Link to="/interviews" className="overline link-underline flex items-center gap-2 shrink-0" data-testid="interviews-link">See all interviews <ArrowUpRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {interviewCards.map((a, i) => (
                <Reveal key={a.id} delay={i * 0.06}>
                  <Link to={`/read/${a.slug}`} className="group block relative overflow-hidden bg-ink" data-testid="interview-card">
                    <div className="relative aspect-[4/5] ed-frame">
                      <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                      <p className="overline text-gloss-gold">5 Questions</p>
                      <h3 className="font-serif text-2xl sm:text-3xl text-cream tracking-tight leading-tight mt-2">{a.title}</h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MID-PAGE STRIP — fixed member advert space */}
      <div className="bg-cream px-5 sm:px-8 pb-6">
        <div className="max-w-[1600px] mx-auto"><MiniAd variant="banner" mode="image" space="home_strip" excludeMemberId={coverSlide?.member_id} /></div>
      </div>

      {/* MEMBERS' DESK */}
      {deskPosts.length > 0 && (
        <section className="bg-cream py-16 sm:py-24 px-5 sm:px-8">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-end justify-between border-b border-ink/20 pb-4 mb-10">
              <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none">Members' Desk</h2>
              <Link to="/members-desk" className="overline link-underline flex items-center gap-2 shrink-0">All columns <ArrowUpRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {deskPosts.map((a, i) => (
                <Reveal key={a.id} delay={i * 0.08}>
                  <Link to={`/read/${a.slug}`} className="group block border border-ink/10 hover:border-ink/40 transition-colors" data-testid="desk-card">
                    <div className="relative overflow-hidden aspect-[4/3] bg-ink">
                      <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-6 sm:p-8">
                      <p className="overline text-gloss-silver mb-4">{a.byline || a.author}</p>
                      <h3 className="font-serif text-2xl sm:text-3xl tracking-tight leading-tight group-hover:text-gloss-gold transition-colors">{a.title}</h3>
                      {a.dek && <p className="font-sans text-base text-ink/60 mt-4 leading-relaxed line-clamp-3">{a.dek}</p>}
                      <span className="inline-flex items-center gap-2 mt-6 overline group-hover:gap-3 transition-all">Read column <ArrowUpRight size={13} /></span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* COVER MEMBERS — random four, reshuffled every visit */}
      {coverMembers.length > 0 && (
      <section className="bg-ink text-cream py-16 sm:py-24 px-5 sm:px-8 relative grain" data-testid="member-covers">
        <div className="max-w-[1600px] mx-auto relative z-10">
          <div className="flex items-end justify-between border-b border-cream/20 pb-4 mb-10">
            <div>
              <p className="overline text-gloss-gold mb-3">The Stars</p>
              <h2 className="font-serif text-4xl sm:text-6xl tracking-tight">Cover Members</h2>
            </div>
              <Link to="/cover-members" className="overline link-underline flex items-center gap-2 text-cream shrink-0">All cover members <ArrowUpRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-8">
            {coverMembers.map((m, i) => (
              <Reveal key={m.id} delay={i * 0.08}>
                <Link to={`/member/${m.id}`} className="group block" data-testid={`member-cover-${i}`}>
                  <div className="relative overflow-hidden aspect-[2/3] bg-ink ed-frame">
                    <img src={imgUrl({ ...m, cover_image: m.directory_image })} alt={m.owner_name} className="w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/85" />
                    {!m.directory_image && (
                      <div className="absolute top-0 left-0 right-0 z-10 flex items-start justify-between px-3 pt-3 gap-2">
                        <span className="font-serif text-cream text-2xl sm:text-3xl leading-none tracking-tight">S/ME</span>
                        <span className="font-mono text-[8px] uppercase tracking-widest text-cream/70 shrink-0">{m.category}</span>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 z-10">
                      <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-widest text-gloss-gold">{m.borough}</p>
                      <p className="font-serif text-base sm:text-xl leading-tight mt-1">{m.owner_name}</p>
                      <p className="font-sans text-[11px] sm:text-xs text-cream/70">{m.business_name}</p>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* BILLBOARD — the big advert band above the footer */}
      <AdBand space="home_billboard" excludeMemberId={coverSlide?.member_id} />

      {/* JOIN CTA */}
      <section className="bg-ink text-cream py-20 sm:py-32 px-5 sm:px-8 relative grain overflow-hidden">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center relative z-10">
          <Reveal className="md:col-span-8">
            <p className="overline text-gloss-gold mb-5">For the UK's independents</p>
            <h2 className="font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
              Become a star.<br />Get seen. Get clients.
            </h2>
            <p className="font-sans text-lg text-cream/80 mt-8 max-w-xl leading-relaxed">
              From £30 a month, our editorial desk turns you into cover-grade editorial — a cover shoot, a written feature, adverts and daily social — while the country reads.
            </p>
          </Reveal>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/join" className="inline-flex items-center gap-3 bg-cream text-ink px-9 py-5 overline hover:bg-gloss-gold transition-colors" data-testid="join-cta">
              Join S/ME <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* S/ME HOUSE ADVERT — its only placement on the site */}
      <HouseAd />
    </MagazineLayout>
  );
}

/* ---------------- Hero Carousel ---------------- */
const DURATION = 7000;
function HeroCarousel({ slides, ready = true }) {
  const [i, setI] = useState(0);
  const has = slides.length > 0;
  const advance = useCallback(() => setI((p) => (p + 1) % Math.max(slides.length, 1)), [slides.length]);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setTimeout(advance, DURATION);
    return () => clearTimeout(t);
  }, [i, advance, slides.length]);

  const s = slides[i];

  return (
    <section className="relative h-screen lg:h-[115vh] w-full overflow-hidden grain bg-ink" data-testid="hero-section">
      <AnimatePresence mode="wait">
        {has && (
          <motion.div
            key={s.id || i}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.9, ease: [0.43, 0.13, 0.23, 0.96] }}
            className="absolute inset-0"
          >
            {s.cover_video ? (
              <video src={assetUrl(s.cover_video)} poster={s.cover_video_poster ? imgUrl({ cover_image: s.cover_video_poster }) : imgUrl(s)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover object-top lg:object-[center_15%] ed-grade" style={s.hero_focus ? { objectPosition: s.hero_focus } : undefined} data-testid="cover-star-video" />
            ) : (
              <img src={imgUrl(s)} alt={s.title} className="absolute inset-0 w-full h-full object-cover object-top lg:object-[center_15%] ed-grade" style={s.hero_focus ? { objectPosition: s.hero_focus } : undefined} data-testid="cover-star-image" />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/20 to-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/40" />
      {has && s.slug && <Link to={`/read/${s.slug}`} className="absolute inset-0 z-10" aria-label={`Read: ${s.title}`} data-testid="hero-cover-overlay-link" />}

      {/* content lockup — single flex column so masthead (top) & cover lines (bottom) never collide */}
      <div className="absolute inset-x-0 top-0 h-screen z-20 flex flex-col justify-between pt-24 sm:pt-28 pb-16 sm:pb-20">
        {/* masthead — anchored LEFT */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="max-w-[1600px] mx-auto w-full px-5 sm:px-8">
            <h1 className="font-serif text-cream text-6xl sm:text-8xl lg:text-[9rem] leading-[0.8] tracking-tighter">S/ME</h1>
            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gloss-gold mt-4">See Me · The UK's Online SME Magazine</p>
            <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.3em] text-cream/60 mt-2" data-testid="hero-issue-line">{editionLabel(cycleIndex)} · Next edition {fmtShort(editionStart(cycleIndex + 1))}</p>
          </div>
        </motion.div>

        {/* cover lines — anchored bottom */}
        <div className="max-w-[1600px] mx-auto w-full px-5 sm:px-8">
          <AnimatePresence mode="wait">
            {has && (
              <motion.div
                key={`txt-${s.id || i}`}
                initial="hidden"
                animate="show"
                exit={{ opacity: 0, y: 20, transition: { duration: 0.3 } }}
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } } }}
                className="max-w-3xl"
              >
                <Line><p className="overline text-cream/80 mb-4">Cover Story · {s.category || "Interview"}</p></Line>
                <Line>
                  <Link to={`/read/${s.slug}`} className="block group/title" data-testid="hero-title-link">
                    <h2 className="font-serif text-cream text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight line-clamp-3 group-hover/title:text-gloss-gold transition-colors">{s.title}</h2>
                  </Link>
                </Line>
                {s.dek && <Line><p className="font-sans text-cream/80 text-lg sm:text-xl mt-5 max-w-2xl leading-relaxed line-clamp-2">{s.dek}</p></Line>}
                <Line>
                  <Link to={`/read/${s.slug}`} className="inline-flex items-center gap-3 mt-8 bg-cream text-ink px-7 py-4 overline hover:bg-gloss-gold transition-colors" data-testid="hero-cta">
                    Read the cover story <ArrowRight size={16} />
                  </Link>
                </Line>
              </motion.div>
            )}
          </AnimatePresence>

          {!has && ready && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="max-w-3xl" data-testid="hero-empty">
              <p className="overline text-cream/80 mb-4">The next issue is being shot</p>
              <h2 className="font-serif text-cream text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">Britain's boldest small businesses, made editorial.</h2>
              <p className="font-sans text-cream/80 text-lg sm:text-xl mt-5 max-w-2xl leading-relaxed">GQ meets Vogue for the UK's independents — cover stars, spotlights and the stories behind the businesses the country loves.</p>
              <Link to="/join" className="inline-flex items-center gap-3 mt-8 bg-cream text-ink px-7 py-4 overline hover:bg-gloss-gold transition-colors" data-testid="hero-join-cta">
                Become a cover star <ArrowRight size={16} />
              </Link>
            </motion.div>
          )}
          {/* progress segments */}
          {slides.length > 1 && (
            <div className="flex gap-2 mt-12" data-testid="hero-progress">
              {slides.map((_, idx) => (
                <button key={idx} onClick={() => setI(idx)} className="h-[3px] w-14 bg-cream/25 relative overflow-hidden" aria-label={`Slide ${idx + 1}`} data-testid={`hero-dot-${idx}`}>
                  {idx === i && <motion.span key={i} initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: DURATION / 1000, ease: "linear" }} className="absolute inset-y-0 left-0 bg-gloss-gold" />}
                  {idx < i && <span className="absolute inset-0 bg-cream/60" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
function Line({ children }) {
  return <motion.div variants={{ hidden: { opacity: 0, y: 26 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } } }}>{children}</motion.div>;
}

/* ---------------- Editor's Manifesto ---------------- */
function EditorsManifesto() {
  const [letter, setLetter] = useState("");
  useEffect(() => {
    client.get("/editors-letter").then((r) => setLetter(r.data.body || "")).catch(() => {});
  }, []);
  if (!letter) return null;
  return (
    <section className="bg-cream py-16 sm:py-20 px-5 sm:px-8 border-y border-ink/10" data-testid="editors-manifesto">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <span className="h-px flex-1 bg-ink/15" />
          <p className="overline text-gloss-silver text-center shrink-0">Editor's Letter · {isPreview ? "Preview Edition" : `Issue Nº ${issueNo}`}</p>
          <span className="h-px flex-1 bg-ink/15" />
        </div>
        <p className="font-serif text-lg sm:text-xl leading-[1.65] tracking-normal text-ink/90 whitespace-pre-line">
          <span className="float-left font-serif text-6xl sm:text-7xl leading-[0.72] text-gloss-gold mr-3 mt-1.5">{letter.charAt(0)}</span>
          {letter.slice(1)}
        </p>
        <div className="flex items-center justify-end gap-4 mt-8">
          <div className="text-right">
            <p className="font-serif text-2xl italic tracking-tight text-ink">The Editors</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50 mt-1">S/ME</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Feature card ---------------- */
function FeatureCard({ a, size = "grid", ratio = "aspect-[3/4]", delay = 0, kicker }) {
  if (!a) return null;
  const titleSize = size === "lead" ? "text-4xl sm:text-6xl" : "text-2xl sm:text-3xl";
  return (
    <Reveal delay={delay}>
      <Link to={`/read/${a.slug}`} className="group block relative overflow-hidden bg-ink" data-testid="article-card">
        <div className={`relative ${ratio} ed-frame`}>
          <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/10" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
          <p className="overline text-gloss-gold">{kicker || a.category}</p>
          <h3 className={`font-serif text-cream tracking-tight leading-[1.02] mt-2 ${titleSize}`}>{a.title}</h3>
          {size === "lead" && a.dek && <p className="font-sans text-base sm:text-lg text-cream/80 mt-3 max-w-xl leading-relaxed">{a.dek}</p>}
          {size === "lead" && <span className="inline-flex items-center gap-2 mt-5 overline text-cream group-hover:gap-3 transition-all">Read the story <ArrowUpRight size={13} /></span>}
        </div>
      </Link>
    </Reveal>
  );
}
